import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/providers/AuthProvider';
import { authFetch } from '@/lib/auth-fetch';
import { getApiBaseUrl } from '@/lib/data-service';
import { fetchFinanceSettings } from '@/lib/finance-settings-storage';
import { fetchUtilisationSettings } from '@/lib/utilisation-settings-storage';
import { fetchBudgetHealthThresholds } from '@/lib/budget-health-thresholds-storage';

interface BootstrapGateProps {
  children: React.ReactNode;
}

type BootstrapState = 'loading' | 'ready' | 'error' | 'no-access' | 'employee-not-found';

const EVO_ORGANISATION_ID_KEY = 'evo_organisation_id';
const PLATFORM_USER_ID_KEY = 'platform_user_id';

interface AuthenticatedUser {
  id: string;
  name: string;
  email: string;
  role: string;
  department?: string;
  photo?: string;
  applicationRole?: string;
  evoOrganisationId?: string;
  platformUserId?: string;
}

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 1000;
const ACCESS_DENIED_MAX_RETRIES = 2;

// Abort the bootstrap employee fetch if the backend does not respond in time.
// Without this, a hung request leaves the whole app stuck on the loading screen
// forever: the await never settles, so the retry logic below never runs. On
// timeout the fetch rejects and falls into the normal retry-then-continue path.
const BOOTSTRAP_FETCH_TIMEOUT_MS = 15000;

/**
 * Reject when `promise` has not settled within `ms`.
 *
 * Every await in the bootstrap has to be bounded, and an `AbortSignal` only
 * covers the request itself: `authFetch` awaits an access token from the host
 * platform first, and the settings prefetches carry no signal at all. A stall
 * in any of them left the app on "Setting up your workspace…" indefinitely,
 * because neither the retry path nor the give-up path could ever run.
 */
function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms);
    promise.then(resolve, reject).finally(() => clearTimeout(timer));
  });
}

// Sentinel ApplicationRole the platform sync stamps when a user's Projects
// access has been revoked ("No Access" in the platform Manage Roles UI).
const NO_ACCESS_ROLE = 'no access';

// A user has no access when they carry no ApplicationRole at all, or the
// explicit revoked sentinel.
export const hasNoApplicationAccess = (applicationRole?: string): boolean => {
  if (!applicationRole) {
    return true;
  }
  return applicationRole.trim().toLowerCase() === NO_ACCESS_ROLE;
};

export function BootstrapGate({ children }: BootstrapGateProps) {
  const { isProduction, setAuthenticatedUser } = useAuth();
  
  const [state, setState] = useState<BootstrapState>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isRetrying, setIsRetrying] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [accessDeniedRetryCount, setAccessDeniedRetryCount] = useState(0);
  const [hasBootstrapped, setHasBootstrapped] = useState(false);

  const bootstrap = useCallback(async (isRetry = false) => {
    if (!isProduction) {
      setState('ready');
      return;
    }

    if (!isRetry) {
      setState('loading');
      setErrorMessage('');
    }
    setIsRetrying(isRetry);

    try {
      const baseUrl = getApiBaseUrl();
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), BOOTSTRAP_FETCH_TIMEOUT_MS);
      let response: Response;
      try {
        response = await withTimeout(
          authFetch(`${baseUrl}/employees/me`, { signal: controller.signal }),
          BOOTSTRAP_FETCH_TIMEOUT_MS,
          'employees/me',
        );
      } finally {
        clearTimeout(timeoutId);
      }
      
      if (!response.ok) {
        if (response.status === 404) {
          if (accessDeniedRetryCount < ACCESS_DENIED_MAX_RETRIES) {
            const nextCount = accessDeniedRetryCount + 1;
            console.warn(`[BootstrapGate] Employee not found (404) — retrying (${nextCount}/${ACCESS_DENIED_MAX_RETRIES})`);
            setAccessDeniedRetryCount(nextCount);
            setTimeout(() => {
              bootstrap(true);
            }, RETRY_DELAY_MS * Math.pow(2, nextCount - 1));
            return;
          }
          console.warn('[BootstrapGate] Employee not found for authenticated user — access denied');
          setHasBootstrapped(true);
          setState('no-access');
          return;
        }

        // Check if this is an "employee not found" 401 from the middleware
        if (response.status === 401) {
          try {
            const body = await response.json();
            if (body?.errorCode === 'EMPLOYEE_NOT_FOUND') {
              console.warn('[BootstrapGate] Employee not found during authentication — user needs to be added by an admin');
              setHasBootstrapped(true);
              setState('employee-not-found');
              return;
            }
          } catch {
            // If we can't parse the body, fall through to the generic error
          }
        }

        throw new Error(`Failed to fetch employee: ${response.status} ${response.statusText}`);
      }

      const employee: AuthenticatedUser = await response.json();
      
      if (employee && employee.id) {
        // Block access if ApplicationRole is null/missing or explicitly "No Access"
        if (hasNoApplicationAccess(employee.applicationRole)) {
          if (accessDeniedRetryCount < ACCESS_DENIED_MAX_RETRIES) {
            const nextCount = accessDeniedRetryCount + 1;
            console.warn(`[BootstrapGate] Employee ${employee.name} (${employee.id}) has no Projects access (ApplicationRole: ${employee.applicationRole ?? 'null'}) — retrying (${nextCount}/${ACCESS_DENIED_MAX_RETRIES})`);
            setAccessDeniedRetryCount(nextCount);
            setTimeout(() => {
              bootstrap(true);
            }, RETRY_DELAY_MS * Math.pow(2, nextCount - 1));
            return;
          }
          console.warn(`[BootstrapGate] Employee ${employee.name} (${employee.id}) has no Projects access (ApplicationRole: ${employee.applicationRole ?? 'null'}) — access denied`);
          setHasBootstrapped(true);
          setState('no-access');
          return;
        }

        setAuthenticatedUser(employee);

        // Store telemetry data in sessionStorage for Application Insights
        if (employee.evoOrganisationId) {
          sessionStorage.setItem(EVO_ORGANISATION_ID_KEY, employee.evoOrganisationId);
        }
        if (employee.platformUserId) {
          sessionStorage.setItem(PLATFORM_USER_ID_KEY, employee.platformUserId);
        }

        console.log(`[BootstrapGate] Successfully loaded employee: ${employee.name} (${employee.id}), role: ${employee.applicationRole}`);

        // Pre-populate settings caches so synchronous get*() calls
        // return the server value from the first render. Best-effort: fetch
        // them together and give up on the warm-up rather than hold the gate
        // open when one of them is slow.
        await withTimeout(
          Promise.allSettled([
            fetchFinanceSettings(),
            fetchUtilisationSettings(),
            fetchBudgetHealthThresholds(),
          ]),
          BOOTSTRAP_FETCH_TIMEOUT_MS,
          'settings prefetch',
        ).catch(() => {});

        setHasBootstrapped(true);
        setRetryCount(0);
        setState('ready');
      } else {
        throw new Error('Invalid employee data received');
      }
    } catch (error) {
      console.error('[BootstrapGate] Failed to fetch employee:', error);
      
      // If we get a JSON parse error (HTML response), it means backend is not available
      // Just continue without employee data rather than blocking the app
      if (error instanceof SyntaxError || (error instanceof Error && error.message.includes('JSON'))) {
        console.warn('[BootstrapGate] Backend not available, continuing without employee data');
        setHasBootstrapped(true);
        setRetryCount(0);
        setState('ready');
        return;
      }
      
      const currentRetryCount = isRetry ? retryCount + 1 : 1;
      setRetryCount(currentRetryCount);
      
      if (currentRetryCount < MAX_RETRIES) {
        console.log(`[BootstrapGate] Retrying in ${RETRY_DELAY_MS}ms (attempt ${currentRetryCount}/${MAX_RETRIES})`);
        setTimeout(() => {
          bootstrap(true);
        }, RETRY_DELAY_MS * Math.pow(2, currentRetryCount - 1));
      } else {
        // After max retries, just continue without employee data
        console.warn('[BootstrapGate] Max retries reached, continuing without employee data');
        setHasBootstrapped(true);
        setRetryCount(0);
        setState('ready');
      }
    }
  }, [isProduction, setAuthenticatedUser, retryCount, accessDeniedRetryCount]);

  const handleRetry = useCallback(() => {
    setRetryCount(0);
    setAccessDeniedRetryCount(0);
    bootstrap(false);
  }, [bootstrap]);

  useEffect(() => {
    if (!hasBootstrapped && isProduction) {
      bootstrap(false);
    } else if (!isProduction) {
      setState('ready');
    }
  }, [isProduction, hasBootstrapped, bootstrap]);

  if (!isProduction) {
    return <>{children}</>;
  }

  if (state === 'loading' || isRetrying) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Setting up your workspace...</p>
          <p className="text-sm text-muted-foreground mt-1">Authenticating and loading your data...</p>
        </div>
      </div>
    );
  }

  if (state === 'no-access') {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="text-center max-w-md p-6">
          <h2 className="text-xl font-semibold text-destructive mb-2">Access Denied</h2>
          <p className="text-muted-foreground mb-4">
            You do not have permission to access this application. Please contact your administrator to request access.
          </p>
          <button
            onClick={handleRetry}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (state === 'employee-not-found') {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="text-center max-w-md p-6">
          <h2 className="text-xl font-semibold text-destructive mb-2">Account Not Found</h2>
          <p className="text-muted-foreground mb-4">
            Your account has not been set up in this application. Please contact your administrator to have your employee record added.
          </p>
          <button
            onClick={handleRetry}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="text-center max-w-md p-6">
          <h2 className="text-xl font-semibold text-destructive mb-2">Unable to Load Application</h2>
          <p className="text-muted-foreground mb-4">
            {errorMessage || 'We encountered an error while loading your data. Please try again.'}
          </p>
          <button
            onClick={handleRetry}
            disabled={isRetrying}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50"
          >
            {isRetrying ? 'Retrying...' : 'Try Again'}
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
